/**
 * VideoSection Component
 * Displays the Wistia video player section
 */

import React, { useEffect } from 'react';
import { string } from 'prop-types';
import { motion } from 'framer-motion';
import AnimatedSection from '../AnimatedSection';
import { VIDEO_CONFIG } from '../../constants/videoConfig';
import { isRecoverableWistiaMediaError } from '../../utils/videoHelpers';
import classes from './VideoSection.module.css';

const appendScriptIfMissing = ({ src, type }) => {
  const existingScript = document.querySelector(`script[src="${src}"]`);
  if (existingScript) {
    return existingScript;
  }

  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  if (type) {
    script.type = type;
  }
  document.head.appendChild(script);
  return script;
};

const VideoSection = ({ className = null }) => {
  useEffect(() => {
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const shouldSuppressWistiaConsoleMessage = args =>
      args.some(arg => isRecoverableWistiaMediaError(arg));
    console.error = (...args) => {
      if (shouldSuppressWistiaConsoleMessage(args)) {
        return;
      }
      originalConsoleError(...args);
    };
    console.warn = (...args) => {
      if (shouldSuppressWistiaConsoleMessage(args)) {
        return;
      }
      originalConsoleWarn(...args);
    };

    const handleWindowError = event => {
      if (isRecoverableWistiaMediaError(event.error || event.message || event)) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };
    const handleUnhandledRejection = event => {
      if (isRecoverableWistiaMediaError(event.reason || event)) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };

    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    appendScriptIfMissing({ src: VIDEO_CONFIG.PLAYER_SCRIPT_URL });
    appendScriptIfMissing({
      src: `https://fast.wistia.com/embed/${VIDEO_CONFIG.EXPLAINER_VIDEO_ID}.js`,
      type: 'module',
    });

    // Add custom styles for video player
    const style = document.createElement('style');
    style.textContent = `
      wistia-player[media-id='${VIDEO_CONFIG.EXPLAINER_VIDEO_ID}']:not(:defined) { 
        background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/${VIDEO_CONFIG.EXPLAINER_VIDEO_ID}/swatch'); 
        display: block; 
        filter: blur(5px); 
        padding-top: 56.25%; 
      }
    `;
    document.head.appendChild(style);

    // Cleanup on unmount
    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      window.removeEventListener('error', handleWindowError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return (
    <AnimatedSection>
      <section className={[classes.videoSection, className].filter(Boolean).join(' ')}>
        <motion.div
          className={classes.videoContent}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div
            className={classes.videoContainer}
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <wistia-player
              media-id={VIDEO_CONFIG.EXPLAINER_VIDEO_ID}
              aspect="1.7777777777777777"
              controls="true"
            />
          </motion.div>
        </motion.div>
      </section>
    </AnimatedSection>
  );
};

VideoSection.propTypes = {
  className: string,
};

export default VideoSection;
